/**
 * Hook to fetch team statistics via server-side get_team_statistics() RPC.
 * Uses Realtime invalidation instead of polling for immediate updates.
 *
 * Replaces the previous implementation that performed 4 client-side queries
 * (profiles, roles, inspections, defects) and heavy client-side aggregation.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useAuth } from "@/hooks/useAuth";

export interface UserStats {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  roles: string[];
  inspections: {
    pending: number;
    inProgress: number;
    completed: number;
    overdue: number;
    total: number;
  };
  defects: {
    open: number;
    inProgress: number;
    resolved: number;
    closed: number;
    total: number;
  };
}

// Internal aggregate shape — only used by useTeamStatisticsQuery below; not exported
// (no cross-module consumer existed; TES-1614 dead-export cleanup).
interface TeamStatisticsTotals {
  inspectionsActive: number;
  inspectionsCompleted: number;
  defectsOpen: number;
  defectsResolved: number;
}

// Query keys — internal to this hook (no cross-module consumer; TES-1614 dead-export cleanup).
const teamStatisticsKeys = {
  all: ["teamStatistics"] as const,
  withTeam: (teamId?: string) => ["teamStatistics", teamId ?? "all"] as const,
};

async function fetchTeamStatistics(teamId?: string): Promise<UserStats[]> {
  const { data, error } = await supabase.rpc(
    "get_team_statistics",
    teamId ? { _team_id: teamId } : {},
  );

  if (error) throw error;

  if (!data || !Array.isArray(data)) {
    return [];
  }

  // The RPC returns jsonb which is already in the correct shape
  return data as unknown as UserStats[];
}

/**
 * Hook to fetch team statistics with TanStack Query.
 * Uses Realtime invalidation instead of polling for immediate updates.
 */
export function useTeamStatisticsQuery(teamId?: string) {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const hasTenant = !!profile?.active_tenant_id;

  const query = useQuery({
    queryKey: teamStatisticsKeys.withTeam(teamId),
    queryFn: () => fetchTeamStatistics(teamId),
    staleTime: 60_000, // 1 minute - prevents excessive refetching on rapid realtime events
    enabled: hasTenant, // Auth-readiness guard: don't call RPC before tenant context is ready
  });

  // Invalidate on defect or inspection changes via centralized Realtime
  useRealtimeSubscription(
    ["defects", "inspections"],
    () => queryClient.invalidateQueries({ queryKey: teamStatisticsKeys.all }),
    { debounceMs: 1000, enabled: hasTenant }, // longer debounce for statistics - doesn't need instant updates
  );

  // Calculate totals from the data
  const totals: TeamStatisticsTotals = (query.data || []).reduce(
    (acc, user) => ({
      inspectionsActive:
        acc.inspectionsActive + user.inspections.pending + user.inspections.inProgress,
      inspectionsCompleted: acc.inspectionsCompleted + user.inspections.completed,
      defectsOpen: acc.defectsOpen + user.defects.open + user.defects.inProgress,
      defectsResolved: acc.defectsResolved + user.defects.resolved + user.defects.closed,
    }),
    { inspectionsActive: 0, inspectionsCompleted: 0, defectsOpen: 0, defectsResolved: 0 },
  );

  return {
    userStats: query.data ?? [],
    totals,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

/**
 * Utility function to get display name for a user.
 */
export function getUserDisplayName(user: UserStats): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) return user.firstName;
  return user.email.split("@")[0];
}
